import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Bookmark, LogOut, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";

// Dot colors follow design_guidelines.json colors.usage (world=blue,
// science=sage, people=amber); tech keeps violet, sports takes coral.
// Keep in sync with CATEGORY_COLORS in StoryCard.jsx.
const NAV_ITEMS = [
  { id: "today", label: "Today", color: "#3A86D4" },
  { id: "world", label: "World", color: "#3A86D4" },
  { id: "science", label: "Science & Wow", color: "#3A9E72" },
  { id: "tech", label: "Tech & Future", color: "#8B5CF6" },
  { id: "sports", label: "Sports", color: "#E8553E" },
  { id: "saved", label: "My Saved", color: "#3A86D4", requiresAuth: true }
];

export const Masthead = ({ lastUpdated, activeCategory, onCategoryChange }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  
  const formatDate = () => {
    const now = new Date();
    return now.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatUpdatedTime = () => {
    if (!lastUpdated) return null;
    const date = new Date(lastUpdated);
    return `Updated ${date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit',
      timeZoneName: 'short'
    })}`;
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <header className="bg-white border-b-2 border-scoop-blue" data-testid="masthead">
      {/* Top Bar */}
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 h-[68px] flex items-center justify-between border-b border-scoop-border">
        {/* Logo */}
        <a 
          href="/" 
          className="font-display font-black text-2xl md:text-[1.9rem] tracking-tight text-scoop-ink"
          data-testid="logo"
        >
          The <em className="italic text-scoop-blue">Scoop</em>
        </a>
        
        {/* Center Info - Hidden on mobile */}
        <div className="hidden md:block text-center">
          <p className="text-[0.68rem] font-semibold tracking-widest uppercase text-gray-500 leading-relaxed">
            {formatDate()} · Safe & Parent-Trusted
          </p>
          {formatUpdatedTime() && (
            <span className="text-scoop-sage font-bold text-[0.68rem] tracking-wider block">
              {formatUpdatedTime()}
            </span>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {user ? (
            <div className="hidden md:flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-xs font-semibold text-scoop-muted hover:text-scoop-blue"
                onClick={() => navigate("/saved")}
                data-testid="saved-btn"
              >
                <Bookmark className="w-4 h-4 mr-1" />
                Saved
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="p-0 h-auto" data-testid="user-menu-btn">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback className="bg-scoop-blue text-white text-xs">
                        {getInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 text-sm font-medium text-scoop-ink">
                    {user.name}
                  </div>
                  <div className="px-2 pb-2 text-xs text-scoop-muted">
                    {user.email}
                  </div>
                  <DropdownMenuItem onClick={() => navigate("/saved")} className="cursor-pointer">
                    <Bookmark className="w-4 h-4 mr-2" />
                    Saved Stories
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="hidden md:flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="text-xs font-semibold text-scoop-blue border-scoop-blue hover:bg-scoop-blue hover:text-white rounded-full"
                onClick={login}
                data-testid="parent-login-btn"
              >
                Parent Login
              </Button>
              <Button
                size="sm"
                className="text-xs font-bold bg-scoop-blue hover:bg-scoop-blue/90 rounded-full"
                onClick={() => (user ? navigate("/saved") : login())}
                data-testid="subscribe-btn"
              >
                Subscribe Free
              </Button>
            </div>
          )}
          
          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] bg-white">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="py-6">
                <div className="font-display font-black text-xl text-scoop-ink mb-6">
                  The <em className="italic text-scoop-blue">Scoop</em>
                </div>
                
                {user && (
                  <div className="flex items-center gap-3 mb-6 pb-4 border-b border-scoop-border">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback className="bg-scoop-blue text-white">
                        {getInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-semibold text-sm text-scoop-ink">{user.name}</div>
                      <div className="text-xs text-scoop-muted">{user.email}</div>
                    </div>
                  </div>
                )}
                
                <nav className="space-y-2">
                  {NAV_ITEMS.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        onCategoryChange(item.id);
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left text-sm font-semibold transition-colors ${
                        activeCategory === item.id 
                          ? "bg-scoop-blue/10 text-scoop-blue" 
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {item.color && (
                        <span 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: item.color }}
                        />
                      )}
                      {item.label}
                    </button>
                  ))}
                </nav>
                
                <div className="mt-8 space-y-2">
                  {user ? (
                    <>
                      <Button 
                        variant="outline" 
                        className="w-full text-scoop-blue border-scoop-blue"
                        onClick={() => {
                          navigate("/saved");
                          setMobileMenuOpen(false);
                        }}
                      >
                        <Bookmark className="w-4 h-4 mr-2" />
                        Saved Stories
                      </Button>
                      <Button 
                        variant="ghost"
                        className="w-full text-red-600 hover:bg-red-50"
                        onClick={() => {
                          logout();
                          setMobileMenuOpen(false);
                        }}
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        variant="outline" 
                        className="w-full text-scoop-blue border-scoop-blue"
                        onClick={login}
                      >
                        Parent Login
                      </Button>
                      <Button
                        className="w-full bg-scoop-blue hover:bg-scoop-blue/90 rounded-full"
                        onClick={() => { login(); setMobileMenuOpen(false); }}
                      >
                        Subscribe Free
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      
      {/* Navigation Bar */}
      <nav 
        className="max-w-[1200px] mx-auto px-5 md:px-10 flex overflow-x-auto scrollbar-hide h-[42px] items-center gap-0.5"
        data-testid="main-nav"
      >
        {NAV_ITEMS.filter(item => !item.requiresAuth || user).map((item) => (
          <button
            key={item.id}
            onClick={() => {
              if (item.id === "saved") {
                navigate("/saved");
              } else {
                onCategoryChange(item.id);
              }
            }}
            className={`flex items-center gap-2 px-4 h-[42px] text-[0.72rem] font-semibold tracking-wider uppercase whitespace-nowrap border-b-2 transition-colors ${
              activeCategory === item.id
                ? "text-scoop-blue border-scoop-blue"
                : "text-gray-500 border-transparent hover:text-scoop-blue"
            }`}
            data-testid={`nav-${item.id}`}
          >
            {item.color && (
              <span 
                className="nav-dot" 
                style={{ backgroundColor: item.color }}
              />
            )}
            {item.label}
          </button>
        ))}
      </nav>
    </header>
  );
};

export default Masthead;
